import { useSyncExternalStore } from "react";

/**
 * Einwilligung in die Nutzungsstatistik (E-33). Ohne Zustimmung meldet der Browser nichts an
 * /api/track, und der Server gibt Spielereignisse dieses Browsers nicht an Matomo weiter
 * (Header `x-numbet-statistik` bzw. Query-Parameter `statistik` am WebSocket).
 * „Do Not Track“ und Global Privacy Control gelten als Ablehnung; dann wird auch nicht gefragt.
 */

export type Consent = "granted" | "denied";

const KEY = "numbet:statistik";
const listeners = new Set<() => void>();
/** Falls localStorage fehlt (private Fenster): Entscheidung nur für diese Sitzung merken. */
let memory: Consent | null = null;
let settingsOpen = false;

export function browserOptOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.doNotTrack === "1" || nav.globalPrivacyControl === true;
}

function stored(): Consent | null {
  try {
    const value = localStorage.getItem(KEY);
    if (value === "granted" || value === "denied") return value;
  } catch {
    // ohne Speicher gilt die Entscheidung aus dem Arbeitsspeicher
  }
  return memory;
}

export function getConsent(): Consent | null {
  return browserOptOut() ? "denied" : stored();
}

export function hasConsent(): boolean {
  return getConsent() === "granted";
}

function notify(): void {
  for (const listener of listeners) listener();
}

export function setConsent(value: Consent): void {
  memory = value;
  try {
    localStorage.setItem(KEY, value);
  } catch {
    // siehe stored()
  }
  settingsOpen = false;
  notify();
}

/** Öffnet den Hinweis erneut, z. B. über „Statistik-Einstellungen“ in der Fußzeile. */
export function openConsentSettings(): void {
  settingsOpen = true;
  notify();
}

export function closeConsentSettings(): void {
  settingsOpen = false;
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useConsent(): Consent | null {
  return useSyncExternalStore(subscribe, getConsent);
}

/** Hinweis zeigen, solange noch nicht entschieden wurde oder die Einstellungen offen sind. */
export function useConsentPrompt(): boolean {
  return useSyncExternalStore(subscribe, () => settingsOpen || getConsent() === null);
}

/** Header für API-Aufrufe, damit der Server Raum-Ereignisse melden darf. */
export function consentHeaders(): Record<string, string> {
  return hasConsent() ? { "x-numbet-statistik": "1" } : {};
}
